import {
  ComprehensiveCompanySearchResponse,
  FinancialInstitution,
  FinancialInstitutionBranch,
} from "../features/api-interfaces";
import {
  DivisionDict,
  getDivisionLabel,
} from "../components/default-constants";
import { DivisionDictDescription } from "../components/merchant-application-direct-corporate-info/constants";
import { every, isEmpty, isNil } from "lodash";

/**
 * 例　ユーザー一覧テーブルヘッダー
 * @param itemsLength 項目個数
 * @param selectedItemsLength 選択された項目の個数
 * @returns (1/3)または(3)のようなテキスト
 */
export const getHeaderCounterText = (
  itemsLength: number,
  selectedItemsLength: number
) => {
  return selectedItemsLength && selectedItemsLength > 0
    ? `(${selectedItemsLength}/${itemsLength})`
    : `(${itemsLength})`;
};

/**
 *
 * @param filteredItems 表示項目
 * @param currentPageIndex 現在のページ
 * @param pageSize ページサイズ
 * @returns ページングされた項目
 */
export const getPagedItems = (
  filteredItems: Object[],
  currentPageIndex: number,
  pageSize: number
) =>
  filteredItems.slice(
    (currentPageIndex - 1) * pageSize,
    currentPageIndex * pageSize
  );

interface Code {
  value: string;
  label: string;
}
/**
 *
 * @param items ターゲットコードリスト
 * @param targetValue　ターゲットコード値
 * @returns ターゲットラベル
 */
export const getCodeLabel = (items: Code[], targetValue: string) => {
  if (!items) return;
  return (
    items.find((item) => String(item.value) === String(targetValue))?.label ||
    targetValue
  );
};
/**
 * ファイル名から拡張子を取得する関数
 * @param filename ファイル名
 * @returns 拡張子
 *
 */
export function getExt(filename: string) {
  let pos = filename.lastIndexOf(".");
  if (pos === -1) return "";
  return filename.slice(pos);
}

// 包括加盟店取得結果変換
export const getComprehensiveMerchantItems = (
  companies: ComprehensiveCompanySearchResponse[] | undefined
): DivisionDict[] => {
  if (companies !== undefined) {
    return companies.map((companies) => ({
      label: companies.comprehensiveCompanyName,
      value: companies.comprehensiveCompanyName,
    }));
  }
  return [];
};

// 包括加盟店取得結果変換
export const getComprehensiveMerchantCodeItems = (
  companies: ComprehensiveCompanySearchResponse[] | undefined
): DivisionDict[] => {
  if (companies !== undefined) {
    return companies.map((companies) => ({
      label: companies.comprehensiveCompanyName,
      value: companies.comprehensiveCompanyCode,
    }));
  }
  return [];
};

/**
 * 金融機関マスタ取得結果変換
 * @param {FinancialInstitution[] | undefined} financialInstitution
 * @returns {DivisionDictDescription[]}
 */
export const getFinancialInstitutionItems = (
  financialInstitution: FinancialInstitution[] | undefined
): DivisionDictDescription[] => {
  if (isEmpty(financialInstitution)) {
    return [];
  }

  return financialInstitution!.map((financialInstitution) => ({
    label: financialInstitution.fnnInsMthStrNmKnj,
    value: financialInstitution.fnnInsMthStrCd,
    description: financialInstitution.fnnInsMthStrCd,
  }));
};

/**
 * 金融機関マスタ支店取得結果変換
 * @param {FinancialInstitutionBranch[] | undefined} financialInstitutionBranch
 * @returns {DivisionDictDescription[]}
 */
export const getFinancialInstitutionBranchItems = (
  financialInstitutionBranch: FinancialInstitutionBranch[] | undefined
): DivisionDictDescription[] => {
  if (isEmpty(financialInstitutionBranch)) {
    return [];
  }

  return financialInstitutionBranch!.map((financialInstitutionBranch) => ({
    label: financialInstitutionBranch.fnnInsBrnKnj,
    value: financialInstitutionBranch.fnnInsBrnCd,
    description: financialInstitutionBranch.fnnInsBrnCd,
  }));
};

/**
 * オブジェクトのすべての値がnullまたはundefinedであるか判定する.
 *
 * 判定したいオブジェクトがfalsyの場合はtrueを返す.
 *
 * @param {Object} obj 判定したいオブジェクト
 * @returns {boolean} 判定結果 true: すべての値がnullまたはundefined
 */
export const isAllValuesNull = (obj?: Object): boolean => {
  if (!obj) {
    return true;
  }

  return every(obj, (value) => isNil(value));
};

/**
 * ページ番号を調整する.
 *
 * 一覧で現在ページ番号にデータがなかった場合、前ページ番号を返す.
 * 調節したページ番号が0だった場合、ページ番号1を返す.
 *
 * @param {number} dataLength 1ページのデータ数
 * @param {number} pageSize ページサイズ
 * @param {number} currentPageIndex 現在のページ番号
 * @returns {number}
 */
export const adjustPageIndex = (
  dataLength: number,
  pageSize: number,
  currentPageIndex: number
): number => {
  const totalPages = Math.ceil(dataLength / pageSize);
  if (totalPages === 0) {
    return 1;
  } else if (currentPageIndex > totalPages) {
    return currentPageIndex - 1;
  } else {
    return currentPageIndex;
  }
};

/**
 * 個別取扱事由のラベルを以下のように返却する.
 * 検索条件指定あり：検索条件の個別取扱事由
 * 検索条件指定なし：表示したいリストの個別取扱事由の/区切り
 * @param {DivisionDict[] | undefined} iHRCodeList コードリストの個別取扱事由
 * @param {string[] | undefined} iHRList 表示したい個別取扱事由コードリスト
 * @param {string} iHRSearchCondition 検索条件の個別取扱事由
 * @returns {string} 個別取扱事由のラベル
 */
export const getIHRLabel = (
  iHRCodeList: DivisionDict[] | undefined,
  iHRList: string[] | undefined,
  iHRSearchCondition: string = ""
): string => {
  if (isNil(iHRCodeList)) {
    return "";
  }
  if (isEmpty(iHRSearchCondition)) {
    return iHRList!
      .map((item) => getDivisionLabel(iHRCodeList, item))
      .join(" / ");
  } else {
    return getDivisionLabel(iHRCodeList, iHRSearchCondition);
  }
};
